/**
 * Collateral Policy API Service
 *
 * Type-safe API client for collateral policy management.
 * Types derived from collateral-policy.openapi.yaml.
 *
 * Endpoints consumed:
 *   GET    /collateral-policy/:tenantId            - Get tenant policy
 *   PUT    /collateral-policy/:tenantId            - Update tenant policy
 *   GET    /collateral-policy/:tenantId/asset-classes  - List asset class policies
 *   PUT    /collateral-policy/:tenantId/asset-classes/:taxonomyNodeId  - Update single
 *   POST   /collateral-policy/:tenantId/validate   - Validate (dry-run)
 *   GET    /collateral-policy/:tenantId/audit      - Audit trail
 *   GET    /collateral-policy/baselines            - Get baseline
 */

import createClient from 'openapi-fetch';
import type { paths } from '@aaimplatform/institutional-types/generated/collateral-policy';
import type { components } from '@aaimplatform/institutional-types/generated/collateral-policy';
import { authMiddleware } from '../client';

export type CollateralPolicy = components['schemas']['CollateralPolicy'];
export type CollateralPolicyUpdate = components['schemas']['CollateralPolicyUpdate'];
export type CollateralPolicySave = components['schemas']['CollateralPolicySave'];
export type PolicyVersionConflict = components['schemas']['PolicyVersionConflict'];
export type AssetClassPolicy = components['schemas']['AssetClassPolicy'];
export type AssetClassPolicyUpdate = components['schemas']['AssetClassPolicyUpdate'];
export type BaselinePolicy = components['schemas']['BaselinePolicy'];
export type PolicyAuditEntry = components['schemas']['PolicyAuditEntry'];
export type PolicyValidationResult = components['schemas']['PolicyValidationResult'];
export type PolicyViolation = components['schemas']['PolicyViolation'];
export type PolicyWarning = components['schemas']['PolicyWarning'];

function createCollateralPolicyClient() {
  const client = createClient<paths>({ baseUrl: '/api/v1', credentials: 'include' });
  client.use(authMiddleware);
  return client;
}

const client = createCollateralPolicyClient();

/**
 * PUT /collateral-policy/{tenantId} answered 409 PolicyVersionConflict: the
 * save named a version that is no longer the tenant's active one, and nothing
 * was written (R1c4c-f1). activeVersion is the version another save made while
 * this page was open; null for both means "no policy".
 */
export class PolicyVersionConflictError extends Error {
  readonly baseVersion: PolicyVersionConflict['baseVersion'];
  readonly activeVersion: PolicyVersionConflict['activeVersion'];

  constructor(conflict: PolicyVersionConflict) {
    super(conflict.message);
    this.name = 'PolicyVersionConflictError';
    this.baseVersion = conflict.baseVersion;
    this.activeVersion = conflict.activeVersion;
  }
}

export const collateralPolicyApi = {
  getPolicy: async (tenantId: string): Promise<CollateralPolicy | null> => {
    const { data, error, response } = await client.GET('/collateral-policy/{tenantId}', {
      params: { path: { tenantId } },
    });
    if (response.status === 404) return null;
    if (error) throw new Error('Failed to fetch collateral policy');
    return data as CollateralPolicy;
  },

  updatePolicy: async (tenantId: string, policy: CollateralPolicySave): Promise<CollateralPolicy> => {
    const { data, error, response } = await client.PUT('/collateral-policy/{tenantId}', {
      params: { path: { tenantId } },
      body: policy,
    });
    if (response.status === 409 && error && (error as PolicyVersionConflict).error === 'POLICY_VERSION_CONFLICT') {
      throw new PolicyVersionConflictError(error as PolicyVersionConflict);
    }
    if (error) {
      if ('violations' in error && error.violations) {
        throw Object.assign(new Error('Policy validation failed'), { violations: error.violations });
      }
      throw new Error('Failed to update collateral policy');
    }
    return data as CollateralPolicy;
  },

  getAssetClasses: async (tenantId: string): Promise<AssetClassPolicy[]> => {
    const { data, error } = await client.GET('/collateral-policy/{tenantId}/asset-classes', {
      params: { path: { tenantId } },
    });
    if (error) throw new Error('Failed to fetch asset class policies');
    return data?.assetClasses ?? [];
  },

  updateAssetClass: async (
    tenantId: string,
    taxonomyNodeId: AssetClassPolicy['taxonomyNodeId'],
    update: AssetClassPolicyUpdate,
  ): Promise<AssetClassPolicy> => {
    const { data, error } = await client.PUT('/collateral-policy/{tenantId}/asset-classes/{taxonomyNodeId}', {
      params: { path: { tenantId, taxonomyNodeId } },
      body: update,
    });
    if (error) throw new Error('Failed to update asset class policy');
    return data as AssetClassPolicy;
  },

  validatePolicy: async (tenantId: string, policy: CollateralPolicyUpdate): Promise<PolicyValidationResult> => {
    const { data, error } = await client.POST('/collateral-policy/{tenantId}/validate', {
      params: { path: { tenantId } },
      body: policy,
    });
    if (error) throw new Error('Failed to validate policy');
    return data as PolicyValidationResult;
  },

  getAuditTrail: async (
    tenantId: string,
    limit = 25,
    offset = 0,
  ): Promise<{ entries: PolicyAuditEntry[]; total: number }> => {
    const { data, error } = await client.GET('/collateral-policy/{tenantId}/audit', {
      params: { path: { tenantId }, query: { limit, offset } },
    });
    if (error) throw new Error('Failed to fetch audit trail');
    return data as { entries: PolicyAuditEntry[]; total: number };
  },

  // The baseline the tenant's institution type selects (R1c3), read for the
  // same tenant the policy is read for. 404 BASELINE_NOT_SELECTED -- the type
  // selects no row -- maps to null. Any other 404 (no institution, a missing
  // row, a path the load balancer does not route) is an error, never a
  // statement about the institution's type.
  getBaseline: async (tenantId: string): Promise<BaselinePolicy | null> => {
    const { data, error, response } = await client.GET('/collateral-policy/baselines', {
      params: { query: { tenantId } },
    });
    if (response.status === 404 && error?.error === 'BASELINE_NOT_SELECTED') return null;
    if (!response.ok || !data) throw new Error('Failed to fetch baseline policy');
    return data;
  },
};
